/**
 * Reglas compartidas: un salón no está disponible si otro grupo activo
 * del mismo plantel/ciclo usa ese salón en un horario que se empalma
 * y comparten al menos un día de clase.
 *
 * El filtro muestra TODOS los salones del plantel y etiqueta
 * (disponible) / (ocupado) cuando hay ciclo + horario para evaluar.
 */
const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo']

export default {
  methods: {
    toMinSalon (t) {
      if (t == null || t === '') return null
      if (t instanceof Date && !isNaN(t.getTime())) {
        return t.getHours() * 60 + t.getMinutes()
      }
      const str = String(t).trim()
      // MySQL / drivers a veces mandan "1970-01-01T08:00:00.000Z"
      const m = str.match(/(\d{1,2}):(\d{2})/)
      if (!m) return null
      return (Number(m[1]) || 0) * 60 + (Number(m[2]) || 0)
    },

    horariosSolapan (inicioA, finA, inicioB, finB) {
      const a0 = this.toMinSalon(inicioA)
      const a1 = this.toMinSalon(finA)
      const b0 = this.toMinSalon(inicioB)
      const b1 = this.toMinSalon(finB)
      if (a0 == null || a1 == null || b0 == null || b1 == null) return false
      return a0 < b1 && a1 > b0
    },

    grupoActivoSn (grupo) {
      if (!grupo) return false
      const a = grupo.activo_sn
      return a == 1 || a === true || a === '1'
    },

    grupoTieneDiasLms (grupo) {
      if (!grupo) return false
      return DIAS_SEMANA.some(d => grupo[d])
    },

    /** Planeación guarda dia como lu1, ma2, sa1… */
    diasDesdeSeleccionPlaneacion (dia) {
      if (!dia) return null
      const key = String(dia).toLowerCase()
      if (key.startsWith('lu')) return { lunes: 1 }
      if (key.startsWith('ma')) return { martes: 1 }
      if (key.startsWith('mi')) return { miercoles: 1 }
      if (key.startsWith('ju')) return { jueves: 1 }
      if (key.startsWith('vi')) return { viernes: 1 }
      if (key.startsWith('sa')) return { sabado: 1 }
      if (key.startsWith('do')) return { domingo: 1 }
      return null
    },

    normalizarGrupoDias (grupo) {
      if (!grupo) return grupo
      if (this.grupoTieneDiasLms(grupo)) return grupo

      const dias = this.diasDesdeSeleccionPlaneacion(grupo.dia)
      if (dias) return { ...grupo, ...dias }

      const nombre = String(grupo.grupo || '').toUpperCase()
      if (nombre.includes('SABATINO') || nombre.includes('SÁBADO') || nombre.includes('SABADO')) {
        return { ...grupo, sabado: 1 }
      }
      if (nombre.includes('DOMINGO')) {
        return { ...grupo, domingo: 1 }
      }

      return grupo
    },

    gruposCompartenDia (grupoA, grupoB) {
      const a = this.normalizarGrupoDias(grupoA)
      const b = this.normalizarGrupoDias(grupoB)
      if (!a || !b) return true

      const aTiene = this.grupoTieneDiasLms(a)
      const bTiene = this.grupoTieneDiasLms(b)

      if (aTiene && bTiene) {
        return DIAS_SEMANA.some(d => a[d] && b[d])
      }

      if (!aTiene && !bTiene) return true

      const conDias = aTiene ? a : b
      const sinDias = aTiene ? b : a
      const sinNorm = this.normalizarGrupoDias(sinDias)

      if (this.grupoTieneDiasLms(sinNorm)) {
        return DIAS_SEMANA.some(d => conDias[d] && sinNorm[d])
      }

      // Grupo sin ficha LMS: no bloquear sábado/domingo de quien sí tiene días
      if (conDias.sabado || conDias.domingo) {
        return false
      }

      return true
    },

    grupoEnTramoHorario (grupo, inicio, fin) {
      if (!this.grupoActivoSn(grupo)) return false
      return this.horariosSolapan(inicio, fin, grupo.hora_inicio, grupo.hora_fin)
    },

    grupoOcupandoSalon (grupo, idSalon, horarioRef, horariosCatalog, excludeGrupoId, grupoRef) {
      if (!this.grupoActivoSn(grupo) || !grupo.id_salon || grupo.id_salon != idSalon) return false
      if (excludeGrupoId && grupo.id_grupo == excludeGrupoId) return false
      if (grupoRef && !this.gruposCompartenDia(grupoRef, grupo)) return false

      if (!horarioRef) {
        return true
      }

      if (horarioRef.id_horario && grupo.id_horario == horarioRef.id_horario) {
        return true
      }

      const catG = (horariosCatalog || []).find(h => h.id_horario == grupo.id_horario) || {}
      const inicioRef = horarioRef.hora_inicio
      const finRef = horarioRef.hora_fin
      const inicioG = grupo.hora_inicio || catG.hora_inicio
      const finG = grupo.hora_fin || catG.hora_fin

      if (inicioRef && finRef && inicioG && finG) {
        return this.horariosSolapan(inicioRef, finRef, inicioG, finG)
      }

      return !!(horarioRef.id_horario && grupo.id_horario == horarioRef.id_horario)
    },

    filterSalonesDisponibles ({
      salones,
      idPlantel,
      idCiclo,
      idHorario,
      idSalonActual,
      grupos,
      excludeGrupoId,
      horariosCatalog,
      grupoRef
    }) {
      if (!idPlantel) {
        return []
      }

      const ref = this.normalizarGrupoDias(grupoRef)

      const lista = (salones || []).filter(s =>
        s.id_plantel == idPlantel &&
        (s.activo_sn == null || s.activo_sn === undefined || s.activo_sn == 1 || s.activo_sn === true)
      )

      const ocupados = new Set()
      const catRef = (horariosCatalog || []).find(h => h.id_horario == idHorario) || {}
      const horaInicio = (ref && ref.hora_inicio) || catRef.hora_inicio
      const horaFin = (ref && ref.hora_fin) || catRef.hora_fin
      const puedeEvaluar = !!idCiclo && !!(idHorario || (horaInicio && horaFin))

      if (puedeEvaluar) {
        const horarioRef = {
          id_horario: idHorario,
          hora_inicio: horaInicio,
          hora_fin: horaFin
        }

        for (const g of (grupos || [])) {
          if (!g || !g.id_salon) continue
          if (g.id_plantel != idPlantel || g.id_ciclo != idCiclo) continue
          if (this.grupoOcupandoSalon(g, g.id_salon, horarioRef, horariosCatalog, excludeGrupoId, ref)) {
            ocupados.add(g.id_salon)
          }
        }
      }

      // Se muestran todos; etiquetas solo si hubo evaluación real (evita falso "disponible")
      return lista
        .map(s => {
          const ocupado = puedeEvaluar && ocupados.has(s.id_salon) && s.id_salon != idSalonActual
          const nombre = String(s.salon || '').trim()
          let salon_label = nombre
          if (puedeEvaluar) {
            salon_label = ocupado ? `${nombre} (ocupado)` : `${nombre} (disponible)`
          }
          return {
            ...s,
            disponible: !ocupado,
            salon_label
          }
        })
        .sort((a, b) => {
          if (puedeEvaluar && a.disponible !== b.disponible) return a.disponible ? -1 : 1
          return String(a.salon || '').localeCompare(String(b.salon || ''), undefined, { numeric: true })
        })
    }
  }
}
